import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

// Exported so the page can build matching FAQPage structured data from the
// exact same questions and answers that render on screen.
export const faqs = [
  {
    q: 'Which market is the strategy built for?',
    a: 'A single instrument: XAUUSD. Focusing on one market lets the logic be tuned to gold price behaviour instead of being spread across many symbols, which keeps the conditions consistent from setup to setup.',
  },
  {
    q: 'Does the strategy trade in both directions?',
    a: 'Yes. The recorded activity includes both long and short positions. It follows the direction that market structure supports at the time rather than only buying into weakness or only selling into strength.',
  },
  {
    q: 'What makes a setup qualify?',
    a: 'A trade has to meet a defined condition framework, combining the market read, the level involved and the reaction at that level, before it is considered. Moves that do not meet those conditions are simply skipped.',
  },
  {
    q: 'How is an open position managed?',
    a: 'Every open position is carried through to its exit as part of the process. Management covers how a position is protected while it is live and when it is closed, so a trade is never left to run without a plan.',
  },
  {
    q: 'Does it trade every day?',
    a: 'No. Trading is selective. When the conditions are not present the strategy stays out of the market, so the number of trades is set by qualified setups rather than by the calendar.',
  },
  {
    q: 'Are the exact internal parameters shared?',
    a: 'No. This page explains the framework behind the trades, but the internal thresholds and settings are not published. What is shown here is the logic, not the configuration values.',
  },
];

function StrategyFaq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="w-full bg-background-100 border-t border-background-200 px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[880px] mx-auto">
        <Reveal>
          <div className="text-center">
            <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
              STRATEGY QUESTIONS
            </span>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-foreground-950 text-[28px] leading-[1.12] sm:text-[36px] lg:text-[42px]">
              Questions About The Strategy.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600 max-w-[580px] mx-auto">
              The things people usually ask about how this gold strategy is structured and when it
              chooses to trade.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            {faqs.map((item, idx) => {
              const isOpen = open === idx;
              return (
                <div key={item.q} className="border-b border-background-200 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="group w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left cursor-pointer"
                  >
                    <span
                      className={`font-heading font-bold tracking-tight text-[15px] md:text-[16px] transition-colors duration-200 ${
                        isOpen ? 'text-primary-700' : 'text-foreground-950 group-hover:text-primary-700'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-background-300 group-hover:border-primary-300'
                      }`}
                    >
                      <i
                        className={`${
                          isOpen ? 'ri-subtract-line text-background-50' : 'ri-add-line text-foreground-500'
                        } text-[15px]`}
                      ></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 md:px-7 pb-6 text-[14px] leading-relaxed text-foreground-600 max-w-[720px]">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default StrategyFaq;